import { EmbeddedService } from '../types/embedding';

/**
 * Environment Configuration
 *
 * Reads configuration from:
 * 1. window.__RUNTIME_CONFIG__ (for Kubernetes runtime injection)
 * 2. import.meta.env (for development with Vite)
 *
 * Runtime config takes precedence over build-time env variables
 */

// Extended Window interface for runtime configuration
interface RuntimeConfig {
  APP_VERSION?: string;
  API_BASE_URL?: string;
  ACL_BASE_URL?: string;
  KEYCLOAK_URL?: string;
  KEYCLOAK_REALM?: string;
  KEYCLOAK_CLIENT_ID?: string;
  FEATURE_COOKIE_CONSENT?: string | boolean;
  FEATURE_DARK_MODE?: string | boolean;
  FEATURE_SERVICE_WORKER?: string | boolean;
  MOCK_API?: string | boolean;
  ASSISTANT_API_BASE_URL?: string;
  ASSISTANT_PUBLIC_TOKEN?: string;
  EMBEDDED_SERVICES?: EmbeddedService[] | string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Application configuration interface
 */
export interface AppConfig {
  /** Current environment */
  environment: 'development' | 'staging' | 'production';
  /** Version baked into the current JS bundle */
  version: string;
  /** Version currently advertised by the deployed runtime */
  runtimeVersion: string;
  /** Base URL for API requests */
  apiBaseUrl: string;
  /** Base URL for ACL / Integration requests */
  aclBaseUrl: string;
  /** API request timeout in milliseconds */
  apiTimeout: number;
  /** Keycloak configuration */
  keycloak: {
    url: string;
    realm: string;
    clientId: string;
  };
  /** Feature flags */
  features: {
    cookieConsent: boolean;
    darkMode: boolean;
    serviceWorker: boolean;
  };
  /** Style service configuration (future) */
  styleService: {
    enabled: boolean;
    url: string;
  };
  /** Embedded services configuration */
  EMBEDDED_SERVICES: EmbeddedService[];
  /** Enable mock API adapter */
  mockApi: boolean;
  assistant: { apiBaseUrl: string; publicToken: string };
}

function toBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() !== 'false';
  }
  return defaultValue;
}

function toEmbeddedServices(value: unknown): EmbeddedService[] {
  if (Array.isArray(value)) {
    return value as EmbeddedService[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed as EmbeddedService[];
      }
    } catch {
      return [];
    }
  }

  return [];
}

/**
 * Get runtime configuration from window object (Kubernetes injection)
 */
function getRuntimeConfig(): Partial<AppConfig> | null {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const runtime = window.__RUNTIME_CONFIG__;
    return {
      runtimeVersion: runtime.APP_VERSION || '',
      apiBaseUrl: runtime.API_BASE_URL,
      aclBaseUrl: runtime.ACL_BASE_URL,
      keycloak: {
        url: runtime.KEYCLOAK_URL || '',
        realm: runtime.KEYCLOAK_REALM || '',
        clientId: runtime.KEYCLOAK_CLIENT_ID || '',
      },
      features: {
        cookieConsent: toBoolean(runtime.FEATURE_COOKIE_CONSENT, true),
        darkMode: toBoolean(runtime.FEATURE_DARK_MODE, true),
        serviceWorker: toBoolean(runtime.FEATURE_SERVICE_WORKER, false),
      },
      mockApi: toBoolean(runtime.MOCK_API, false),
      assistant: {
        apiBaseUrl:
          runtime.ASSISTANT_API_BASE_URL ??
          import.meta.env.VITE_ASSISTANT_API_BASE_URL ??
          'https://app.elunos.ai',
        publicToken:
          runtime.ASSISTANT_PUBLIC_TOKEN ??
          import.meta.env.VITE_ASSISTANT_PUBLIC_TOKEN ??
          'wgt_dee8b51208903454119e1c8ec08cb50b',
      },
      EMBEDDED_SERVICES: toEmbeddedServices(runtime.EMBEDDED_SERVICES),
    };
  }
  return null;
}

/**
 * Get configuration from Vite environment variables
 */
function getEnvConfig(): AppConfig {
  return {
    environment: (import.meta.env.VITE_APP_ENV as AppConfig['environment']) || 'development',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    runtimeVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
    aclBaseUrl: import.meta.env.VITE_ACL_BASE_URL || '',
    apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
    keycloak: {
      url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
      realm: import.meta.env.VITE_KEYCLOAK_REALM || 'shell-realm',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'shell-client',
    },
    features: {
      cookieConsent: import.meta.env.VITE_FEATURE_COOKIE_CONSENT !== 'false',
      darkMode: import.meta.env.VITE_FEATURE_DARK_MODE !== 'false',
      serviceWorker: import.meta.env.VITE_FEATURE_SERVICE_WORKER === 'true',
    },
    styleService: {
      enabled: import.meta.env.VITE_STYLE_SERVICE_ENABLED === 'true',
      url: import.meta.env.VITE_STYLE_SERVICE_URL || '',
    },
    mockApi: import.meta.env.VITE_MOCK_API === 'true',
    assistant: {
      apiBaseUrl: import.meta.env.VITE_ASSISTANT_API_BASE_URL ?? 'https://app.elunos.ai',
      publicToken:
        import.meta.env.VITE_ASSISTANT_PUBLIC_TOKEN ?? 'wgt_dee8b51208903454119e1c8ec08cb50b',
    },
    EMBEDDED_SERVICES: [
      {
        id: 'community',
        name: 'Community',
        type: 'iframe',
        url: 'https://community.dev.portal.ottrobotics.de',
        passAuth: true,
        passConsent: true,
        styles: {
          minHeight: '800px',
        },
      },
      {
        id: 'launchpad',
        name: 'LaunchPad',
        type: 'iframe',
        url: 'http://localhost:5175',
        passAuth: true,
        passConsent: true,
        permissions: ['portal:launchpad:view', 'launchpad:view'],
        styles: {
          minHeight: '860px',
        },
      },
      {
        id: 'mock-app',
        name: 'Mock Application',
        type: 'iframe',
        url: '/mock-app.html',
        passAuth: true,
        passConsent: true,
        styles: {
          minHeight: '600px',
        },
      },
      {
        id: 'shop',
        name: 'Shop',
        type: 'iframe',
        url: 'http://localhost:5174',
        passAuth: true,
        passConsent: true,
        permissions: ['portal:shop:view', 'shop:view'],
        styles: {
          minHeight: '820px',
        },
      },
      {
        id: 'portfolio',
        name: 'Portfolio',
        type: 'iframe',
        url: 'http://localhost:5180',
        passAuth: true,
        passConsent: true,
        permissions: ['portal:portfolio:view', 'portfolio:view'],
        styles: {
          minHeight: '820px',
        },
      },
      {
        id: 'library',
        name: 'Library',
        type: 'iframe',
        url: 'http://localhost:5181',
        passAuth: true,
        passConsent: true,
        permissions: ['portal:library:view'],
        styles: {
          minHeight: '820px',
        },
      },
      {
        id: 'booking',
        name: 'Booking',
        type: 'iframe',
        url: 'http://localhost:5182',
        passAuth: true,
        passConsent: true,
        permissions: ['portal:booking:view', 'portal:booking:action', 'portal:booking:admin'],
        styles: {
          minHeight: '820px',
        },
      },
    ],
  };
}

/**
 * Merged application configuration
 * Runtime config (Kubernetes) takes precedence over build-time env vars
 */
export function getConfig(): AppConfig {
  const envConfig = getEnvConfig();
  const runtimeConfig = getRuntimeConfig();

  if (runtimeConfig) {
    return {
      ...envConfig,
      ...runtimeConfig,
      keycloak: {
        ...envConfig.keycloak,
        ...runtimeConfig.keycloak,
      },
      features: {
        ...envConfig.features,
        ...runtimeConfig.features,
      },
    };
  }

  return envConfig;
}

/**
 * Singleton instance of the configuration
 */
let configInstance: AppConfig | null = null;

/**
 * Get the application configuration (cached)
 */
export function config(): AppConfig {
  if (!configInstance) {
    configInstance = getConfig();
  }
  return configInstance;
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return config().environment === 'development';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return config().environment === 'production';
}

export default config;
